import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { format, startOfDay } from 'date-fns';
import type { CalendarView } from '@/components/calendar/CalendarToolbar';

export type DayLayout = 'horizontal' | 'vertical';

interface CalendarState {
  calendarDate: string; // ISO date string "YYYY-MM-DD"
  /**
   * The view the user picked THEMSELVES, or null when they have not picked one
   * (#4121). Deliberately not an "effective view": the workshop's default is
   * resolved at read time (`useWorkshopCalendarView`), so a default that loads
   * or changes after first paint never overwrites a click, and a null here is
   * never mistaken for a choice.
   */
  calendarView: CalendarView | null;
  /** `tenantId:userId` the explicit view belongs to; a different account ignores it. */
  calendarViewScope: string | null;
  dayLayout: DayLayout;
  showExtendedHours: boolean; // Show hours outside business hours
  /**
   * Compress the closed parts of the horizontal time axis so working hours get
   * the space (#1313). Off by default — it changes how the whole timeline reads,
   * so it should be something the user turns on.
   */
  compressClosedHours: boolean;
  setCalendarDate: (date: Date) => void;
  setCalendarView: (view: CalendarView, scope: string) => void;
  setDayLayout: (layout: DayLayout) => void;
  setShowExtendedHours: (show: boolean) => void;
  setCompressClosedHours: (compress: boolean) => void;
}

export const useCalendarStore = create<CalendarState>()(
  persist(
    (set) => ({
      calendarDate: format(startOfDay(new Date()), 'yyyy-MM-dd'),
      calendarView: null,
      calendarViewScope: null,
      dayLayout: 'horizontal',
      showExtendedHours: false,
      compressClosedHours: false,
      setCalendarDate: (date) =>
        set({ calendarDate: format(startOfDay(date), 'yyyy-MM-dd') }),
      setCalendarView: (view, scope) => set({ calendarView: view, calendarViewScope: scope }),
      setDayLayout: (layout) => set({ dayLayout: layout }),
      setShowExtendedHours: (show) => set({ showExtendedHours: show }),
      setCompressClosedHours: (compress) => set({ compressClosedHours: compress }),
    }),
    {
      name: 'staff-portal-calendar',
      // v1 (#4121): `calendarView` used to hold the hardcoded 'week' default
      // from first render on, so a stored value cannot be told from a real
      // choice. It is dropped; the workshop default decides until the user clicks.
      version: 1,
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Record<string, unknown>;
        if (version < 1) {
          delete state['calendarView'];
          delete state['calendarViewScope'];
        }
        return state as unknown as CalendarState;
      },
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        calendarDate: state.calendarDate,
        calendarView: state.calendarView,
        calendarViewScope: state.calendarViewScope,
        dayLayout: state.dayLayout,
        showExtendedHours: state.showExtendedHours,
        compressClosedHours: state.compressClosedHours,
      }),
    }
  )
);
